import { useEffect } from 'react';

/**
 * Carry the reader to what a disclosure just revealed (SPEC-025 D-21).
 *
 * Opening a plant card renders the whole plant BELOW the first screen — the card
 * keeps its picture and its brief exactly where they were (D-19), which is what
 * makes opening a transition rather than a jump. The cost of that is a
 * disclosure whose result is off screen: the reader lifts the card, the card
 * opens, and nothing they can see has changed.
 *
 * So the page follows the gesture up. Smoothly, unlike `useScrollReset` beside
 * it, and the difference is the whole point of each: that one is a different page
 * ARRIVING and animating it would mean watching the last plant leave; this one is
 * the reader moving through a page they are still on, and the travel is what
 * tells them where they went.
 *
 * `clearance` is how much of the top belongs to something else. Measured by the
 * caller rather than assumed here: this hook knows about scrolling, not about
 * which chrome a particular screen floats over its own scrollport.
 *
 * The app scrolls in `.app-shell__content`, not the window — `useParallaxScene`
 * and `useScrollReset` both already know this and read the same container the
 * same way.
 */
export default function useScrollReveal(isRevealed, targetRef, {
    clearance = () => 0,
    getScrollContainer = defaultScrollContainer,
    prefersReducedMotion = defaultPrefersReducedMotion,
} = {}) {
    useEffect(() => {
        const target = targetRef.current;
        const container = getScrollContainer();

        if (!isRevealed || !target || !container?.scrollTo) {
            return undefined;
        }

        // After the frame that mounted it: the element exists as soon as React
        // commits, but its box does not until layout has run, and a scroll aimed
        // at a box of zero height lands at the top of the page.
        const frame = requestAnimationFrame(() => {
            // Less whatever floats over the top of the scrollport. The way back
            // is sticky there and owns no space in the flow, so a target landed
            // flush with the top of the viewport lands UNDER it — and what the
            // reader was carried here to read begins behind a button.
            const top = Math.max(0, offsetWithin(target, container) - clearance());

            container.scrollTo({
                top,
                behavior: prefersReducedMotion() ? 'auto' : 'smooth',
            });
        });

        return () => cancelAnimationFrame(frame);
    // Only when the disclosure OPENS. Closing it unmounts what this points at,
    // and the page shrinks back to one screen on its own.
    }, [isRevealed]);
}

/**
 * Where `element` sits inside the box that scrolls it.
 *
 * Measured rather than read off `offsetTop`, which is relative to whichever
 * ancestor happens to be positioned — and on this page several are.
 */
function offsetWithin(element, container) {
    const box = element.getBoundingClientRect();

    if (container === window) {
        return box.top + window.scrollY;
    }

    return box.top - container.getBoundingClientRect().top + container.scrollTop;
}

function defaultScrollContainer() {
    if (typeof document === 'undefined') {
        return null;
    }

    return document.querySelector('.app-shell__content') || window;
}

function defaultPrefersReducedMotion() {
    return typeof window !== 'undefined'
        && typeof window.matchMedia === 'function'
        && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}
